/**
 * ProductEditor.jsx
 *
 * Modal editor for creating/editing products
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useAlert } from '../../providers/AlertProvider';
import { createProduct, updateProduct } from '../../api/enhancedWarehouse';
import { ProductEditorFunctions } from '../../functions/warehouse_page/product_editor_functions';
import logger from "../../utils/logger";


export default function ProductEditor({
    product = null,
    allCustomers = [],
    existingProducts = [], // Added for validation
    isCustomer = false,
    userProfile = null,
    color,
    onSave,
    onClose,
    canEdit = true,
}) {
    const { showAlert } = useAlert();
    const productFunctions = new ProductEditorFunctions(showAlert);
    const [form, setForm] = useState({
        product_code: "",
        product_name: "",
        customer_id: "",
        description: "",
        barcode: "",
        batch_no: "",
        weight_kg: "",
        dimensions_cm: "",
        pallet_spaces: 1,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // Helper function to check if batch number is required
    const isBatchNumberRequired = (productCode, currentProductId = null) => {
        if (!productCode.trim()) return false;
        
        const duplicateProducts = existingProducts.filter(p => 
            p.product_code?.toUpperCase() === productCode.toUpperCase() && 
            p.id !== currentProductId
        );
        
        return duplicateProducts.length > 0;
    };

    useEffect(() => {
        if (product) {
            setForm({
                product_code: product.product_code || "",
                product_name: product.product_name || "",
                customer_id: product.customer_id || "",
                description: product.description || "",
                barcode: product.barcode || "",
                batch_no: product.batch_no || "",
                weight_kg: product.weight_kg || "",
                dimensions_cm: product.dimensions_cm || "",
                pallet_spaces: product.pallet_spaces || 1,
            });
        } else if (isCustomer && userProfile?.customer_id) {
            setForm((current) => ({
                ...current,
                customer_id: String(userProfile.customer_id),
            }));
        }
    }, [product, isCustomer, userProfile?.customer_id]);

    // Handle form field changes with validation clearing
    const handleFieldChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        
        // Clear errors for this field and related fields
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                
                // Clear batch_no error if product_code changes
                if (field === 'product_code' && newErrors.batch_no) {
                    delete newErrors.batch_no;
                }
                
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Enhanced validation with product code + batch number logic
        const newErrors = {};
        if (!form.product_code.trim()) {
            newErrors.product_code = "Product code is required";
        }
        if (!form.product_name.trim()) {
            newErrors.product_name = "Product name is required";
        }
        if (!form.customer_id) {
            newErrors.customer_id = "Customer is required";
        }
        
        // Check if batch number is required for duplicate product codes
        const trimmedProductCode = form.product_code.trim();
        if (trimmedProductCode && isBatchNumberRequired(trimmedProductCode, product?.id)) {
            if (!form.batch_no?.trim()) {
                newErrors.batch_no = "Batch number is required when product code already exists";
            } else {
                // Check if this product_code + batch_no combination already exists
                const duplicateCombo = existingProducts.find(p => 
                    p.product_code?.toUpperCase() === trimmedProductCode.toUpperCase() &&
                    p.batch_no?.toUpperCase() === form.batch_no.trim().toUpperCase() &&
                    p.id !== product?.id
                );
                
                if (duplicateCombo) {
                    newErrors.batch_no = "This product code and batch number combination already exists";
                }
            }
        }
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        try {
            // Prepare form data, removing empty strings for optional fields
            const formData = { ...form };
            
            // Convert text fields to uppercase
            formData.product_code = formData.product_code.trim().toUpperCase();
            formData.product_name = formData.product_name.trim().toUpperCase();
            if (formData.description && formData.description.trim()) {
                formData.description = formData.description.trim().toUpperCase();
            } else {
                formData.description = null;
            }
            if (formData.barcode && formData.barcode.trim()) {
                formData.barcode = formData.barcode.trim().toUpperCase();
            } else {
                formData.barcode = null;
            }
            if (formData.batch_no && formData.batch_no.trim()) {
                formData.batch_no = formData.batch_no.trim().toUpperCase();
            } else {
                formData.batch_no = null;
            }
            if (formData.dimensions_cm && formData.dimensions_cm.trim()) {
                formData.dimensions_cm = formData.dimensions_cm.trim().toUpperCase();
            } else {
                formData.dimensions_cm = null;
            }
            
            // Handle numeric fields
            if (!formData.weight_kg) formData.weight_kg = null;
            formData.pallet_spaces = parseInt(formData.pallet_spaces) || 1;

            if (isCustomer) {
                delete formData.customer_id;
                delete formData.pallet_spaces;
            }
            
            let response;
            
            if (product) {
                response = await updateProduct(product.id, formData);
            } else {
                response = await createProduct(formData);
            }

            if (response.success || response.status === 200 || response.status === 201) {
                showAlert({
                    message: product ? "Product updated successfully" : "Product created successfully",
                    type: "success",
                    buttons: [{ label: "Close", type: "primary" }],
                });
                onSave();
            } else {
                throw new Error(response.message || 'Operation failed');
            }
        } catch (error) {
            logger.error('Error saving product:', error);
            showAlert({
                message: `Failed to save product: ${error.message}`,
                type: "danger",
                buttons: [{ label: "Close", type: "primary" }],
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteProduct = async () => {
        await productFunctions.deleteProduct(product?.id, onSave, product, setLoading);
    };

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: "1rem",
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                style={{
                    backgroundColor: color.panel,
                    color: color.fg,
                    border: `2px solid ${color.border}`,
                    borderRadius: 8,
                    width: "100%",
                    maxWidth: "600px",
                    maxHeight: "90vh",
                    overflow: "auto",
                }}
            >
                <div
                    style={{
                        borderBottom: `1px solid ${color.border}`,
                        padding: "1rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <h2 style={{ margin: 0 }}>
                        {product ? `Edit Product: ${product.product_code}` : "Create New Product"}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: "none",
                            border: "none",
                            fontSize: "1.5rem",
                            cursor: "pointer",
                            color: color.fg,
                        }}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: "1rem" }}>
                    <fieldset disabled={!canEdit} style={{ border: "none", padding: 0, margin: 0, display: "contents" }}>
                    <div style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr 1fr", 
                        gap: "1rem",
                        marginBottom: "1rem"
                    }}>
                        <div>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                                Product Code *
                                {isBatchNumberRequired(form.product_code, product?.id) && (
                                    <span style={{ 
                                        color: '#ffc107', 
                                        fontSize: '0.8rem', 
                                        marginLeft: '0.5rem' 
                                    }}>
                                        (Duplicate code)
                                    </span>
                                )}
                            </label>
                            <input
                                type="text"
                                value={form.product_code}
                                onChange={(e) => handleFieldChange('product_code', e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    border: `1px solid ${errors.product_code ? '#dc3545' : color.border}`,
                                    borderRadius: 4,
                                    backgroundColor: color.bg,
                                    color: color.fg,
                                }}
                                placeholder="e.g., WIDGET-001"
                            />
                            {errors.product_code && (
                                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                    {errors.product_code}
                                </div>
                            )}
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                                Product Name *
                            </label>
                            <input
                                type="text"
                                value={form.product_name}
                                onChange={(e) => setForm(prev => ({ ...prev, product_name: e.target.value }))}
                                style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    border: `1px solid ${errors.product_name ? '#dc3545' : color.border}`,
                                    borderRadius: 4,
                                    backgroundColor: color.bg,
                                    color: color.fg,
                                }}
                                placeholder="e.g., Blue Widget"
                            />
                            {errors.product_name && (
                                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                    {errors.product_name}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ marginBottom: "1rem" }}>
                        <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                            Customer *
                        </label>
                        <select
                            value={form.customer_id}
                            onChange={(e) => setForm(prev => ({ ...prev, customer_id: e.target.value }))}
                            disabled={isCustomer}
                            style={{
                                width: "100%",
                                padding: "0.5rem",
                                border: `1px solid ${errors.customer_id ? '#dc3545' : color.border}`,
                                borderRadius: 4,
                                backgroundColor: color.bg,
                                color: color.fg,
                            }}
                        >
                            <option value="">Select Customer</option>
                            {isCustomer && form.customer_id && !allCustomers.some(
                                (customer) => String(customer.id) === String(form.customer_id)
                            ) && (
                                <option value={form.customer_id}>Your company</option>
                            )}
                            {allCustomers.map(customer => (
                                <option key={customer.id} value={customer.id}>
                                    {customer.company}
                                </option>
                            ))}
                        </select>
                        {errors.customer_id && (
                            <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                {errors.customer_id}
                            </div>
                        )}
                    </div>

                    <div style={{ marginBottom: "1rem" }}>
                        <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                            Description
                        </label>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                            style={{
                                width: "100%",
                                padding: "0.5rem",
                                border: `1px solid ${color.border}`,
                                borderRadius: 4,
                                backgroundColor: color.bg,
                                color: color.fg,
                                minHeight: "80px",
                                resize: "vertical",
                            }}
                            placeholder="Optional product description..."
                        />
                    </div>

                    <div style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr 1fr 1fr", 
                        gap: "1rem",
                        marginBottom: "1rem"
                    }}>
                        <div>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                                Barcode
                            </label>
                            <input
                                type="text"
                                value={form.barcode}
                                onChange={(e) => setForm(prev => ({ ...prev, barcode: e.target.value }))}
                                style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    border: `1px solid ${color.border}`,
                                    borderRadius: 4,
                                    backgroundColor: color.bg,
                                    color: color.fg,
                                }}
                                placeholder="Optional..."
                            />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                                Batch Number
                                {isBatchNumberRequired(form.product_code, product?.id) && (
                                    <span style={{ color: '#dc3545' }}> *</span>
                                )}
                            </label>
                            <input
                                type="text"
                                value={form.batch_no}
                                onChange={(e) => handleFieldChange('batch_no', e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    border: `1px solid ${errors.batch_no ? '#dc3545' : color.border}`,
                                    borderRadius: 4,
                                    backgroundColor: color.bg,
                                    color: color.fg,
                                }}
                                placeholder={isBatchNumberRequired(form.product_code, product?.id) 
                                    ? "Required for duplicate product codes..." 
                                    : "Optional..."}
                            />
                            {errors.batch_no && (
                                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                    {errors.batch_no}
                                </div>
                            )}
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                                Weight (kg)
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={form.weight_kg}
                                onChange={(e) => setForm(prev => ({ ...prev, weight_kg: e.target.value }))}
                                style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    border: `1px solid ${color.border}`,
                                    borderRadius: 4,
                                    backgroundColor: color.bg,
                                    color: color.fg,
                                }}
                                placeholder="0.00"
                                min="0"
                            />
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                                Dimensions (cm)
                            </label>
                            <input
                                type="text"
                                value={form.dimensions_cm}
                                onChange={(e) => setForm(prev => ({ ...prev, dimensions_cm: e.target.value }))}
                                style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    border: `1px solid ${color.border}`,
                                    borderRadius: 4,
                                    backgroundColor: color.bg,
                                    color: color.fg,
                                }}
                                placeholder="L x W x H"
                            />
                        </div>
                    </div>

                    {!isCustomer && (
                        <div style={{
                            marginBottom: "1rem",
                            padding: "0.75rem",
                            border: `1px solid ${form.pallet_spaces > 1 ? '#ffc107' : color.border}`,
                            borderRadius: 4,
                            backgroundColor: form.pallet_spaces > 1 ? 'rgba(255,193,7,0.08)' : 'transparent',
                        }}>
                            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: canEdit ? "pointer" : "default", marginBottom: 0 }}>
                                <input
                                    type="checkbox"
                                    checked={form.pallet_spaces > 1}
                                    onChange={(e) => setForm(prev => ({ ...prev, pallet_spaces: e.target.checked ? 2 : 1 }))}
                                    disabled={!canEdit}
                                />
                                <span style={{ fontWeight: "bold" }}>Bulk Product</span>
                                <span style={{ fontSize: "0.8rem", color: color.muted || '#888' }}>
                                    (occupies more than 1 pallet space — charged accordingly)
                                </span>
                            </label>
                            {form.pallet_spaces > 1 && (
                                <div style={{ marginTop: "0.75rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
                                    <label style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Pallet Spaces *</label>
                                    <input
                                        type="number"
                                        min="2"
                                        max="99"
                                        value={form.pallet_spaces}
                                        onChange={(e) => setForm(prev => ({ ...prev, pallet_spaces: Math.max(2, parseInt(e.target.value) || 2) }))}
                                        disabled={!canEdit}
                                        style={{
                                            width: "80px",
                                            padding: "0.375rem 0.5rem",
                                            border: `1px solid #ffc107`,
                                            borderRadius: 4,
                                            backgroundColor: color.bg,
                                            color: color.fg,
                                        }}
                                    />
                                    <span style={{ fontSize: "0.85rem", color: '#ffc107' }}>
                                        This product will be charged as {form.pallet_spaces}× the standard storage rate
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    </fieldset>

                    <div style={{ 
                        display: "flex", 
                        gap: "0.5rem", 
                        justifyContent: "flex-end",
                        paddingTop: "1rem",
                        borderTop: `1px solid ${color.border}`
                    }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: "0.5rem 1rem",
                                border: `1px solid ${color.border}`,
                                backgroundColor: color.bg,
                                color: color.fg,
                                borderRadius: 4,
                                cursor: "pointer",
                            }}
                        >
                            Cancel
                        </button>
                        
                        {/* Delete button - only show for existing products */}
                        {product && product.id && canEdit && !isCustomer && (
                            <button
                                type="button"
                                onClick={handleDeleteProduct}
                                disabled={loading}
                                style={{
                                    padding: "0.5rem 1rem",
                                    border: "none",
                                    backgroundColor: "#dc3545", // Red color for delete
                                    color: "#fff",
                                    borderRadius: 4,
                                    cursor: loading ? "not-allowed" : "pointer",
                                    opacity: loading ? 0.7 : 1,
                                }}
                            >
                                {loading ? "Deleting..." : "Delete Product"}
                            </button>
                        )}
                        
                        {canEdit && (
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    padding: "0.5rem 1rem",
                                    border: "none",
                                    backgroundColor: color.success,
                                    color: "#fff",
                                    borderRadius: 4,
                                    cursor: loading ? "not-allowed" : "pointer",
                                    opacity: loading ? 0.7 : 1,
                                }}
                            >
                                {loading ? "Saving..." : (product ? "Update Product" : "Create Product")}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

ProductEditor.propTypes = {
    product: PropTypes.object,
    allCustomers: PropTypes.arrayOf(PropTypes.object).isRequired,
    existingProducts: PropTypes.arrayOf(PropTypes.object),
    isCustomer: PropTypes.bool,
    userProfile: PropTypes.object,
    color: PropTypes.object.isRequired,
    canEdit: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
